/**
 * window.__cogoRuntime API — programmatic form control for Radix components
 * (Select, Checkbox, Switch) that don't have native DOM input elements,
 * and general runtime state/event access for browser automation.
 */

/** Minimal element interface for tree walking */
interface ElementLike {
  id: string
  bind?: string
  children?: ElementLike[]
}

/** Minimal StateManager interface — accepts both @cogo/runtime and @cogo/runtime-v2 */
interface StateManagerLike {
  setState(path: string, value: unknown): void
  get(path: string): unknown
  getSnapshot(): Record<string, unknown>
}

export interface CogoRuntimeApi {
  setValue(elementId: string, value: unknown): { success: boolean; error?: string; bindPath?: string }
  getValue(elementId: string): unknown
  dispatchEvent(elementId: string, eventType: string, payload?: unknown): void
  getState(path?: string): unknown
  ready: boolean
}

declare global {
  interface Window {
    __cogoRuntime?: CogoRuntimeApi
  }
}

/**
 * Walk block UI trees to find an element by ID (DFS).
 * Supports both v1 (block.root) and v2 (block.ui.root) block shapes.
 */
function findElementById(blocks: Record<string, unknown>, elementId: string): ElementLike | undefined {
  for (const block of Object.values(blocks)) {
    const b = block as Record<string, unknown>
    // v2: block.ui.root | v1: block.root
    const root = (b.ui as Record<string, unknown>)?.root ?? b.root
    if (!root) continue
    const found = walkTree(root as ElementLike, elementId)
    if (found) return found
  }
  return undefined
}

function walkTree(node: ElementLike, targetId: string): ElementLike | undefined {
  if (node.id === targetId) return node
  if (node.children) {
    for (const child of node.children) {
      const found = walkTree(child, targetId)
      if (found) return found
    }
  }
  const tmpl = (node as unknown as Record<string, unknown>).template
  if (tmpl) {
    const found = walkTree(tmpl as ElementLike, targetId)
    if (found) return found
  }
  return undefined
}

export interface CogoRuntimeApiOptions {
  stateManager: StateManagerLike
  /** Lazy block getter — blocks may update after initial install (background loading).
   *  Supports both v1 ({root}) and v2 ({ui:{root}}) block shapes. */
  getBlocks: () => Record<string, unknown>
  /** Event dispatch function — bridges to the runtime's EventDispatcher.
   *  Falls back to window.__cogo_tools__?.dispatchEvent if not provided. */
  dispatchEvent?: (elementId: string, eventType: string, payload?: unknown) => void
}

/**
 * Install window.__cogoRuntime API using the Cogo renderer runtime.
 * Returns a cleanup function.
 *
 * Coexists with window.__cogo_tools__ (webmcp-polyfill) — they serve different purposes.
 * __cogo_tools__ is the full WebMCP tool manifest; __cogoRuntime is a lightweight
 * setValue/getValue API primarily for browser automation (Playwright/WebMCP).
 */
export function installCogoRuntimeApi(opts: CogoRuntimeApiOptions): () => void {
  if (typeof window === "undefined") return () => {}

  const { stateManager, getBlocks, dispatchEvent: externalDispatch } = opts

  // Resolve event dispatch: use provided function, or fallback to __cogo_tools__
  const dispatch = (elementId: string, eventType: string, payload?: unknown) => {
    if (externalDispatch) {
      externalDispatch(elementId, eventType, payload)
    } else if ((window as any).__cogo_tools__?.dispatchEvent) {
      ;(window as any).__cogo_tools__.dispatchEvent(elementId, eventType, payload)
    }
  }

  const api: CogoRuntimeApi = {
    setValue(elementId, value) {
      const blocks = getBlocks()
      const element = findElementById(blocks, elementId)
      if (!element) return { success: false, error: `Element "${elementId}" not found` }
      if (!element.bind) return { success: false, error: `Element "${elementId}" has no bind property` }
      const bindPath = element.bind.startsWith("#{") ? element.bind.slice(2, -1) : element.bind
      stateManager.setState(bindPath, value)
      dispatch(elementId, "change", { value })
      return { success: true, bindPath }
    },

    getValue(elementId) {
      const blocks = getBlocks()
      const element = findElementById(blocks, elementId)
      if (!element?.bind) return undefined
      const bindPath = element.bind.startsWith("#{") ? element.bind.slice(2, -1) : element.bind
      return stateManager.get(bindPath)
    },

    dispatchEvent(elementId, eventType, payload) {
      dispatch(elementId, eventType, payload)
    },

    getState(path) {
      if (path) return stateManager.get(path)
      return stateManager.getSnapshot()
    },

    ready: true,
  }

  window.__cogoRuntime = api
  return () => {
    if (window.__cogoRuntime === api) {
      delete window.__cogoRuntime
    }
  }
}
